import * as React from 'react';
import classNames from 'classnames';

import {
  InjectedFocusableProps,
  withFocusable,
  WithFocusableProps,
} from '../../common/Focusable';
import { st, classes } from './ButtonCore.st.css';
import { dataHooks } from './constants';
import { isStatelessComponent } from './utils';

export type ButtonProps = {
  /** an element type to render as (string or function).  */
  as?: string | React.ComponentType<any>;
  children?: React.ReactNode;
  /** URL of the page that link goes to */
  href?: string;
  /** accepts prefix icon */
  prefixIcon?: React.ReactElement;
  /** accepts suffix icon  */
  suffixIcon?: React.ReactElement;
  /** apply disabled styles */
  disabled?: boolean;
  /** class name to be added to the content span */
  contentClassName?: string;
  /** React ref to be attached to the content span */
  contentRef?: React.RefObject<HTMLSpanElement>;
} & Exclude<React.ButtonHTMLAttributes<unknown>, WithFocusableProps> &
  WithFocusableProps<HTMLButtonElement>;

const _addAffix = (
  Affix: React.ReactElement,
  styleClass: string,
  dataHook: string,
) =>
  Affix &&
  React.cloneElement(Affix, {
    className: classNames(classes[styleClass], Affix.props.className),
    'data-hook': Affix.props['data-hook'] || Affix.props.dataHook || dataHook,
  });

/**
 * ButtonCore
 */
class ButtonCore extends React.Component<InjectedFocusableProps<ButtonProps>> {
  static defaultProps = {
    as: 'button',
    type: 'button' as 'button' | 'submit' | 'reset' | undefined,
  };
  static displayName = 'ButtonCore';
  public innerComponentRef: React.RefObject<HTMLElement | React.ComponentType> =
    React.createRef();

  focus() {
    if (this.innerComponentRef && (this.innerComponentRef as any).focus) {
      (this.innerComponentRef as any).focus();
    }
  }

  /**
   * Returns the size and position of the element
   */
  getBoundingClientRect(): DOMRect | undefined {
    if (
      this.innerComponentRef &&
      (this.innerComponentRef as any).getBoundingClientRect
    ) {
      return (this.innerComponentRef as any).getBoundingClientRect();
    }

    return;
  }

  render() {
    const {
      as: Component,
      suffixIcon,
      prefixIcon,
      children,
      disabled,
      focusableOnFocus,
      focusableOnBlur,
      href,
      contentClassName,
      contentRef,
      onClick,
      onKeyDown,
      onKeyUp,
      ...rest
    } = this.props;
    const htmlTabIndex = rest.tabIndex || 0;
    const htmlHref = disabled ? undefined : href;
    const reference =
      isStatelessComponent(Component) && typeof Component !== 'string'
        ? undefined
        : (ref: React.RefObject<HTMLElement | React.ComponentType>) =>
            (this.innerComponentRef = ref);

    // Block action handlers when disabled (using aria-disabled instead of native disabled)
    const handleClick = disabled
      ? (e: React.MouseEvent) => e.preventDefault()
      : onClick;
    const handleKeyDown = disabled
      ? (e: React.KeyboardEvent<HTMLElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
          }
        }
      : onKeyDown;

    if (!Component) {
      return null;
    }

    return (
      <Component
        data-hook={dataHooks.button}
        {...rest}
        onFocus={focusableOnFocus}
        onBlur={focusableOnBlur}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onKeyUp={disabled ? undefined : onKeyUp}
        href={htmlHref}
        ref={reference}
        tabIndex={htmlTabIndex}
        aria-disabled={disabled || undefined}
        className={st(classes.root, { disabled }, this.props.className)}
      >
        {prefixIcon && _addAffix(prefixIcon, 'prefix', dataHooks.prefixIcon)}
        <span
          className={st(classes.content, contentClassName)}
          ref={contentRef}
        >
          {children}
        </span>
        {suffixIcon && _addAffix(suffixIcon, 'suffix', dataHooks.suffixIcon)}
      </Component>
    );
  }
}

export default withFocusable<ButtonProps, HTMLButtonElement>(ButtonCore);
